import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const COUNT_ELEMENT_WIDTH = 36 // Fallback width for the count element while it is not rendered
const GAP_WIDTH = 4 // Gap width between items

type EllipsisContent = {
    totalContentWidth: number
    displayedContentWidth: number
    hiddenCount: number
    maxItemWidth: number
}

function calculateVisibleItems(containerElement: HTMLDivElement, countElementWidth: number): EllipsisContent {
    const containerWidth = containerElement.getBoundingClientRect().width

    // Width of each child element
    const itemWidths = Array.from(containerElement.children).map(child => child.getBoundingClientRect().width)

    const maxItemWidth = Math.round(containerWidth - countElementWidth - GAP_WIDTH)

    const content = itemWidths.reduce(
        (acc, itemWidth, index) => {
            // Calculate new total when adding item
            const total = acc.totalContentWidth + Math.min(itemWidth, maxItemWidth) + GAP_WIDTH

            const isLast = index === itemWidths.length - 1

            // The last element could fit if count element is not needed
            const overflow = isLast ? total > containerWidth : total > maxItemWidth

            // Hide item if total exceeds container width
            if (overflow && index !== 0) {
                acc.hiddenCount += 1
            } else {
                // Update displayed content width if item fits
                acc.displayedContentWidth = total
            }

            // Update total width of all items
            acc.totalContentWidth = total

            return acc
        },
        { totalContentWidth: 0, hiddenCount: 0, displayedContentWidth: 0, maxItemWidth }
    )

    // Round so sub-pixel jitter between measurements does not defeat the equality guards below
    return { ...content, displayedContentWidth: Math.round(content.displayedContentWidth) }
}

function getCountElementWidthOrDefault(countRef: RefObject<HTMLDivElement | null>): number {
    const measuredCountWidth = countRef.current?.getBoundingClientRect().width
    return measuredCountWidth ? Math.ceil(measuredCountWidth) : COUNT_ELEMENT_WIDTH
}

export const useEllipsis = () => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const countRef = useRef<HTMLDivElement | null>(null)

    const [hiddenCount, setHiddenCount] = useState(0)
    const [displayedWidth, setDisplayedWidth] = useState<number | undefined>(undefined)
    const [maxItemWidth, setMaxItemWidth] = useState<number | undefined>(undefined)

    const measure = useCallback(() => {
        const containerElement = containerRef.current
        if (!containerElement) return

        const countElementWidth = getCountElementWidthOrDefault(countRef)

        const next = calculateVisibleItems(containerElement, countElementWidth)

        setHiddenCount(next.hiddenCount)
        setDisplayedWidth(next.displayedContentWidth)
        setMaxItemWidth(next.maxItemWidth)
    }, [])

    useLayoutEffect(measure)

    useEffect(() => {
        const root = containerRef.current
        if (!root || typeof ResizeObserver === 'undefined') return
        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(root)
        return () => resizeObserver.disconnect()
    }, [measure])

    return { containerRef, countRef, hiddenCount, displayedWidth, maxItemWidth }
}
